// Client-side outage evidence recorder. When the edge layer / backend is down,
// nothing server-side can log what users saw — so each browser records failed
// backend requests (exact status: 502, 520, network/TLS failure) into
// localStorage and uploads them to /api/telemetry/client-error once the app is
// reachable again. Evidence survives tab closes and full outages.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORE_KEY = 'wos_client_telemetry_v1';
const MAX_BUFFER = 300;
const FLUSH_BATCH = 50;
const TELEMETRY_PATH = '/api/telemetry/client-error';

const sessionId = Math.random().toString(36).slice(2, 10);
// Persistent per-browser id: production clients often share one edge/LB IP, so
// the server rate-limits per (ip, bid) instead of per IP — otherwise a
// post-outage flush from hundreds of browsers collapses into one bucket and
// most evidence is silently dropped (429).
let browserId = '';
try {
  browserId = localStorage.getItem('wos_bid') || '';
  if (!browserId) {
    browserId = Math.random().toString(36).slice(2, 12);
    localStorage.setItem('wos_bid', browserId);
  }
} catch {}
// Lazy: Tauri injects its globals via init script — checking at module-load
// time could race it. Evaluated per-record instead.
const isDesktop = () =>
  typeof window !== 'undefined' && !!(window.__TAURI__ || window.__TAURI_INTERNALS__);

let lastRecordKeyTs = {}; // throttle: 1 record per 5s per path+status

const readBuffer = () => {
  try { return JSON.parse(localStorage.getItem(STORE_KEY)) || []; } catch { return []; }
};
const writeBuffer = (buf) => {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(buf.slice(-MAX_BUFFER))); } catch {}
};

export const recordClientFailure = ({ url, method, status, kind, message, durationMs }) => {
  let path = url || '';
  try { path = new URL(url, BACKEND_URL).pathname; } catch {}
  if (path.startsWith('/api/telemetry')) return; // never record our own uploads
  const key = `${path}|${status}|${kind}`;
  const now = Date.now();
  if (lastRecordKeyTs[key] && now - lastRecordKeyTs[key] < 5000) return;
  if (Object.keys(lastRecordKeyTs).length > 500) lastRecordKeyTs = {}; // bound long-session growth
  lastRecordKeyTs[key] = now;
  const buf = readBuffer();
  buf.push({
    ts: new Date().toISOString(),
    kind: kind || 'http_error',
    status: status || 0,
    method: (method || 'GET').toUpperCase(),
    path,
    message: (message || '').slice(0, 300),
    duration_ms: Math.round(durationMs || 0),
    page: window.location.pathname,
    app: isDesktop() ? 'desktop' : 'web',
    session: sessionId,
    online: navigator.onLine,
  });
  writeBuffer(buf);
};

let flushing = false;
export const flushClientTelemetry = async () => {
  if (flushing || !BACKEND_URL) return;
  if (!readBuffer().length) return;
  flushing = true;
  try {
    // Drain in batches; stop on any failure (events stay buffered for later).
    for (let i = 0; i < 6; i++) {
      const cur = readBuffer();
      if (!cur.length) break;
      const batch = cur.slice(0, FLUSH_BATCH);
      const res = await fetch(`${BACKEND_URL}${TELEMETRY_PATH}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events: batch, bid: browserId }),
      });
      if (!res.ok) break;
      writeBuffer(readBuffer().slice(batch.length));
      if (batch.length < FLUSH_BATCH) break;
    }
  } catch {} finally {
    flushing = false;
  }
};

export const initClientTelemetry = () => {
  // Upload anything that survived a previous outage, then keep draining.
  setTimeout(flushClientTelemetry, 5000);
  setInterval(flushClientTelemetry, 60000);
};
